import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getSelection,
  $isNodeSelection,
  $getNodeByKey,
  SELECTION_CHANGE_COMMAND,
  COMMAND_PRIORITY_LOW,
} from 'lexical';
import { mergeRegister } from '@lexical/utils';
import { createPortal } from 'react-dom';
import { $isImageNode } from '../../LexicalEditor/nodes/ImageNode';
import { $isEmbedNode } from '../../LexicalEditor/nodes/EmbedNode';
import {
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
} from '../../icons';
import { getScrollableAncestors } from '../../../utils/helpers';
import './scss/NodeSelectionToolbar.scss';

const WIDTH_OPTIONS = [
  { label: 'Small', value: 350 },
  { label: 'Medium', value: 550 },
  { label: 'Large', value: 750 },
  { label: 'Full', value: '100%' },
];

function NodeSelectionToolbar({ editor }) {
  const toolbarRef = useRef(null);
  const [selectedNodeKey, setSelectedNodeKey] = useState(null);
  const [nodeType, setNodeType] = useState(null);
  const [currentAlignment, setCurrentAlignment] = useState(null);
  const [currentWidth, setCurrentWidth] = useState(null);
  const [showWidthMenu, setShowWidthMenu] = useState(false);
  const widthMenuRef = useRef(null);

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();

    if ($isNodeSelection(selection)) {
      const nodes = selection.getNodes();
      if (nodes.length === 1) {
        const node = nodes[0];
        if ($isImageNode(node)) {
          setSelectedNodeKey(node.getKey());
          setNodeType('image');
          setCurrentAlignment(node.getAlignment() || null);
          const w = node.__width;
          setCurrentWidth(w === 'inherit' ? '100%' : w);
          return;
        }
        if ($isEmbedNode(node)) {
          setSelectedNodeKey(node.getKey());
          setNodeType('embed');
          setCurrentAlignment(node.getAlignment() || null);
          setCurrentWidth(node.__width);
          return;
        }
      }
    }

    // No valid node selected — hide
    setSelectedNodeKey(null);
    setNodeType(null);
    setCurrentAlignment(null);
    setCurrentWidth(null);
    setShowWidthMenu(false);
  }, []);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      }),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateToolbar();
          return false;
        },
        COMMAND_PRIORITY_LOW
      )
    );
  }, [editor, updateToolbar]);

  // Position the toolbar above the selected node
  useEffect(() => {
    const toolbarElem = toolbarRef.current;
    if (!toolbarElem) return;

    if (!selectedNodeKey) {
      toolbarElem.style.opacity = '0';
      toolbarElem.style.pointerEvents = 'none';
      return;
    }

    const positionToolbar = () => {
      const domElement = editor.getElementByKey(selectedNodeKey);

      if (!domElement) {
        toolbarElem.style.opacity = '0';
        toolbarElem.style.pointerEvents = 'none';
        return;
      }

      const rect = domElement.getBoundingClientRect();
      const toolbarWidth = toolbarElem.offsetWidth;
      const toolbarHeight = toolbarElem.offsetHeight;

      // Use viewport-relative coords (position: fixed)
      const top = rect.top - toolbarHeight - 8;
      let left = rect.left + rect.width / 2 - toolbarWidth / 2;

      // Keep within viewport
      const viewportWidth = window.innerWidth;
      if (left < 10) left = 10;
      if (left + toolbarWidth > viewportWidth - 10) {
        left = viewportWidth - toolbarWidth - 10;
      }

      toolbarElem.style.opacity = '1';
      toolbarElem.style.pointerEvents = 'auto';
      toolbarElem.style.top = `${top}px`;
      toolbarElem.style.left = `${left}px`;
    };

    // Small delay to ensure DOM is ready after state change
    requestAnimationFrame(positionToolbar);

    // Reposition on scroll/resize — attach to all scrollable ancestors like the popover system
    const domElement = editor.getElementByKey(selectedNodeKey);
    const scrollableAncestors = getScrollableAncestors(domElement);
    const handleReposition = () => requestAnimationFrame(positionToolbar);

    scrollableAncestors.forEach(ancestor => ancestor.addEventListener('scroll', handleReposition));
    window.addEventListener('resize', handleReposition);

    return () => {
      scrollableAncestors.forEach(ancestor => ancestor.removeEventListener('scroll', handleReposition));
      window.removeEventListener('resize', handleReposition);
    };
  }, [editor, selectedNodeKey, currentAlignment, currentWidth]);

  // Close width menu on outside click
  useEffect(() => {
    if (!showWidthMenu) return;
    const handleClickOutside = (e) => {
      if (widthMenuRef.current && !widthMenuRef.current.contains(e.target)) {
        setShowWidthMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showWidthMenu]);

  const setAlignment = (alignment) => {
    if (!selectedNodeKey) return;
    editor.update(() => {
      const node = $getNodeByKey(selectedNodeKey);
      if ($isImageNode(node) || $isEmbedNode(node)) {
        node.setAlignment(alignment);
      }
    });
    setCurrentAlignment(alignment);
  };

  const setWidth = (width) => {
    if (!selectedNodeKey) return;
    editor.update(() => {
      const node = $getNodeByKey(selectedNodeKey);
      if ($isImageNode(node)) {
        node.setWidthAndHeight(width, 'inherit');
      } else if ($isEmbedNode(node)) {
        node.setWidthAndHeight(width, node.__height);
      }
    });
    setCurrentWidth(width);
    setShowWidthMenu(false);
  };

  const getActiveWidthLabel = () => {
    for (const opt of WIDTH_OPTIONS) {
      if (opt.value === currentWidth) return opt.label;
      if (typeof opt.value === 'number' && typeof currentWidth === 'number' && Math.abs(opt.value - currentWidth) < 20) {
        return opt.label;
      }
    }
    return 'Width';
  };

  const isVisible = selectedNodeKey !== null;

  return createPortal(
    <div
      ref={toolbarRef}
      className="node-selection-toolbar"
      style={{ opacity: 0, pointerEvents: 'none' }}
      onMouseDown={(e) => e.preventDefault()}
    >
      {isVisible && (
        <div className="node-toolbar-content">
          {/* Alignment buttons */}
          <button
            type="button"
            className={`node-toolbar-button ${currentAlignment === 'left' ? 'active' : ''}`}
            onClick={() => setAlignment('left')}
            title="Align left"
          >
            <FormatAlignLeft size={16} color="currentColor" />
          </button>
          <button
            type="button"
            className={`node-toolbar-button ${currentAlignment === 'center' || !currentAlignment ? 'active' : ''}`}
            onClick={() => setAlignment('center')}
            title="Align center"
          >
            <FormatAlignCenter size={16} color="currentColor" />
          </button>
          <button
            type="button"
            className={`node-toolbar-button ${currentAlignment === 'right' ? 'active' : ''}`}
            onClick={() => setAlignment('right')}
            title="Align right"
          >
            <FormatAlignRight size={16} color="currentColor" />
          </button>

          <div className="node-toolbar-divider" />

          {/* Width dropdown */}
          <div className="node-toolbar-dropdown-wrapper" ref={widthMenuRef}>
            <button
              type="button"
              className="node-toolbar-button node-toolbar-width-trigger"
              onClick={() => setShowWidthMenu(!showWidthMenu)}
              title="Set width"
            >
              <span className="node-toolbar-width-label">{getActiveWidthLabel()}</span>
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ marginLeft: 2 }}>
                <path d="M2.5 4L5 6.5L7.5 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            {showWidthMenu && (
              <div className="node-toolbar-dropdown">
                {WIDTH_OPTIONS.map((opt) => (
                  <button
                    key={opt.label}
                    type="button"
                    className={`node-toolbar-dropdown-item ${currentWidth === opt.value ? 'active' : ''}`}
                    onClick={() => setWidth(opt.value)}
                  >
                    <span>{opt.label}</span>
                    <span className="node-toolbar-dropdown-hint">
                      {typeof opt.value === 'number' ? `${opt.value}px` : opt.value}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}

export default function NodeSelectionToolbarPlugin() {
  const [editor] = useLexicalComposerContext();
  return <NodeSelectionToolbar editor={editor} />;
}
